/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { ApiError, GetTenantActivityResponse, TelemetryActivitySection, TelemetryActivityWindowParam } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export const TENANT_ACTIVITY_QUERY_KEY_ROOT = 'tenantActivity';

export const ACTIVITY_WINDOWS = [
  '1h',
  '3h',
  '12h',
  '24h',
  '7d',
  '14d',
  '30d',
] as const satisfies readonly TelemetryActivityWindowParam[];

export const DEFAULT_ACTIVITY_WINDOW: TelemetryActivityWindowParam = '7d';

const PAGE_SIZE = 20;

const ACTIVITY_WINDOW_STORAGE_KEY = 'dashboard.activity.window';

type ActivityPageParam = {
  offset: number;
  since?: string;
  until?: string;
};

export type UseTenantActivityInfiniteQueryParams = {
  tenantId: string | undefined;
  section: TelemetryActivitySection;
  activityWindow: TelemetryActivityWindowParam;
};

export function useTenantActivityInfiniteQuery({
  tenantId,
  section,
  activityWindow,
}: UseTenantActivityInfiniteQueryParams) {
  const subtenantId = useMgmtPlaneStateStore((state) => state.selectedSubtenantID);

  return useInfiniteQuery<
    GetTenantActivityResponse,
    ApiError,
    InfiniteData<GetTenantActivityResponse, ActivityPageParam>,
    readonly unknown[],
    ActivityPageParam
  >({
    queryKey: [TENANT_ACTIVITY_QUERY_KEY_ROOT, tenantId, section, activityWindow, subtenantId],
    enabled: !!tenantId,
    initialPageParam: { offset: 0 },
    queryFn: ({ pageParam }) =>
      mgmtPlaneAPIClient.telemetry.getTenantActivity({
        tenantId: tenantId!,
        section,
        window: activityWindow,
        offset: pageParam.offset,
        limit: PAGE_SIZE,
        since: pageParam.since,
        until: pageParam.until,
        subtenantId,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.meta.hasMore) return undefined;
      const offset = allPages.reduce((sum, page) => sum + page.data.length, 0);
      const firstMeta = allPages[0].meta;
      return { offset, since: firstMeta.since, until: firstMeta.until };
    },
  });
}

export function isActivityWindow(value: string | null): value is TelemetryActivityWindowParam {
  return value !== null && (ACTIVITY_WINDOWS as readonly string[]).includes(value);
}

function readStoredWindow(fallback: TelemetryActivityWindowParam): TelemetryActivityWindowParam {
  try {
    const value = window.sessionStorage.getItem(ACTIVITY_WINDOW_STORAGE_KEY);
    return isActivityWindow(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

export type UseActivityWindow = {
  activityWindow: TelemetryActivityWindowParam;
  setActivityWindow: (value: TelemetryActivityWindowParam) => void;
};

export function useActivityWindow(
  defaultWindow: TelemetryActivityWindowParam = DEFAULT_ACTIVITY_WINDOW,
): UseActivityWindow {
  const [activityWindow, setActivityWindowState] = useState<TelemetryActivityWindowParam>(() =>
    readStoredWindow(defaultWindow),
  );

  const setActivityWindow = useCallback((value: TelemetryActivityWindowParam) => {
    try {
      window.sessionStorage.setItem(ACTIVITY_WINDOW_STORAGE_KEY, value);
    } catch {
      // sessionStorage may be unavailable (private mode); ignore.
    }
    setActivityWindowState(value);
  }, []);

  return { activityWindow, setActivityWindow };
}

/**
 * Returns a callable that resets every tenant-activity query for the given
 * tenant back to its initial page params and refetches. Use for the page's
 * "Refresh" action so we keep the same queryKey (no cache churn) while still
 * forcing a fresh page-1 load for both Hallmarks and Deflections.
 */
export function useRefreshTenantActivity(tenantId: string | undefined): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    if (!tenantId) return;
    queryClient.resetQueries({ queryKey: [TENANT_ACTIVITY_QUERY_KEY_ROOT, tenantId] });
  }, [queryClient, tenantId]);
}
